import { useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { CSSObject, keyframes } from '@emotion/react'
import { ProductInfo } from '../ProductInfo'
import ProductImages from './ProductImages'
import { useCheckout, useModal } from 'shop/hooks'
import { CategoryProduct } from '../Shop/Categories'
import CloseButton from '../ProductInfo/Header/CloseButton'
import useEscModalHandler from 'shop/hooks/useEscModalHandler'
import { StyledHTMLElement } from 'shop/theme/types'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

const ProductModal = (product: CategoryProduct) => {
  const params = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const mobileScrollRef = useRef<HTMLDivElement>(null)
  const { closeModal, isModalOpen } = useModal()
  const { productError } = useCheckout()
  const [isClosing, setIsClosing] = useState(false)

  const productName = product.name
  const imageUrls = product.images

  const parentUrl = params['slug'] ? `/store/${params['slug']}` : `/`

  const productLimitReachedError =
    productError?.message === 'PRODUCT_LIMIT_REACHED'

  const handleCloseModal = (e?: React.MouseEvent) => {
    // If the product limit error modal is active, do not allow users to close product modal
    if (productLimitReachedError) return
    closeModal('fulfillment')
    e?.preventDefault()
    e?.stopPropagation()
    setIsClosing(true)
    // Allow css transition to complete before navigating
    setTimeout(() => {
      navigate({
        pathname: parentUrl,
        search: location.search
      })
    }, 250)
  }

  useEffect(() => {
    // if ProductModal is opened from UpsellModal do not lock/unlock body scroll.
    if (isModalOpen('upsell')) return
    // onMount lock scroll behind ProductModal
    document.body.style.overflow = 'hidden'
    return () => {
      // onDismount unlock scroll that was behind ProductModal
      document.body.style.overflow = 'visible'
    }
  }, [])

  useEscModalHandler({ onClose: handleCloseModal })

  // Do not render product modal if product is not defined
  if (!product?.id) return null

  return (
    <>
      <ContainerOverlay data-testid='productModal' onClick={handleCloseModal} />
      <Container
        hasImages={!!imageUrls?.length}
        ref={mobileScrollRef}
        id='product-modal-container'
        isClosing={isClosing}
      >
        <CloseButton onClick={handleCloseModal} />
        {!!imageUrls?.length && (
          <ProductImages imageUrls={imageUrls} productName={productName} />
        )}
        <ProductInfo
          product={product}
          closeModal={handleCloseModal}
          mobileScrollRef={mobileScrollRef}
        />
      </Container>
    </>
  )
}

const ContainerOverlay = styled.section(
  (): CSSObject => ({
    background: 'rgba(0, 0, 0, 0.8)',
    height: '100%',
    maxHeight: '100vh',
    width: '100%',
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 100,
    overflow: 'hidden'
  })
)

export const Container = styled.section<
  StyledHTMLElement & { hasImages: boolean; isClosing: boolean }
>(({ theme, hasImages, isClosing }) => ({
  animation: `${isClosing ? slideDownMobile : slideUpMobile} 300ms ease-out`,
  zIndex: theme.zIndex.productModal,
  maxWidth: theme.designMode ? '1220px' : '100%',
  display: 'block',
  flexDirection: 'column',
  position: 'fixed',
  height: '100%',
  top: '0',
  width: '100%',
  overflow: 'scroll',
  background: 'white',
  [theme.mediaQueries.viewport6]: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    height: '100%',
    margin: 'auto',
    width: '62vw'
  },

  [theme.mediaQueries.viewport9]: {
    margin: '0',
    height: hasImages ? '46vw' : '90%',
    display: 'flex',
    border: '0 0 24px #2626268c',
    borderRadius: theme.borderRadius,
    boxShadow: '12px 24px 24px #262626b0',
    overflow: 'hidden',
    flexDirection: 'row',
    position: 'fixed',
    width: hasImages ? '90vw' : '680px',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    animation: `${isClosing ? slideDownDesktop : slideUpDesktop} 300ms ease-out`,
    maxHeight: '1024px',
    maxWidth: '1600px'
  }
}))

const slideUpDesktop = keyframes`
  0% {
    opacity: 0;
    transform: translate(-50%, 120%)
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%)
  }`

const slideUpMobile = keyframes`
  0% {
    opacity: 0;
    transform: translateY(120%)
  }
  100% {
    opacity: 1;
    transform: translateY(0)
  }`

const slideDownMobile = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0)
  }
  100% {
    opacity: 0;
    transform: translateY(120%)
  }`

const slideDownDesktop = keyframes`
  0% {
    opacity: 1;
    transform: translate(-50%, -50%)
  }
  100% {
    opacity: 0;
    transform: translate(-50%, 120%)
  }`

export default ProductModal
